import { API_BASE } from '../config.js';
import type { Circuit, CircuitGenerationParams } from '@multirx/shared/types/track.js';
import type { CircuitBackupFile } from '@multirx/shared/game/circuitBackup.js';

export type CircuitStatus = 'published' | 'pending' | 'rejected' | 'archived';

export interface SavedCircuit {
  id: number;
  created_at: string;
  name?: string | null;
  params: CircuitGenerationParams;
  circuit: Circuit;
  /** Présent sur GET /api/circuits/:id — nombre de replays liés au circuit. */
  replayCount?: number;
  status?: CircuitStatus;
  created_by?: number | null;
}

export async function fetchCircuits(limit?: number): Promise<SavedCircuit[]> {
  const query = limit != null ? `?limit=${encodeURIComponent(String(limit))}` : '';
  const res = await fetch(`${API_BASE}/api/circuits${query}`);
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error((err as { error?: string }).error ?? 'Erreur lors du chargement des circuits');
  }
  return res.json() as Promise<SavedCircuit[]>;
}

export async function generateAndSaveCircuit(
  params: CircuitGenerationParams
): Promise<SavedCircuit> {
  const res = await fetch(`${API_BASE}/api/generate-track`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error((err as { error?: string }).error ?? 'Erreur lors de la génération');
  }
  const data = (await res.json()) as Circuit & { id: number };
  const { id, ...circuit } = data;
  return {
    id,
    created_at: new Date().toISOString(),
    params,
    circuit,
  };
}

export async function fetchCircuitById(id: number): Promise<SavedCircuit> {
  const res = await fetch(`${API_BASE}/api/circuits/${id}`);
  if (!res.ok) {
    if (res.status === 404) throw new Error('Circuit non trouvé');
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error((err as { error?: string }).error ?? 'Erreur lors du chargement du circuit');
  }
  return res.json() as Promise<SavedCircuit>;
}

export async function updateCircuit(
  id: number,
  circuit: Circuit,
  params?: CircuitGenerationParams
): Promise<SavedCircuit> {
  const res = await fetch(`${API_BASE}/api/circuits/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ circuit, params }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(
      (err as { error?: string }).error ?? 'Erreur lors de la mise à jour du circuit'
    );
  }
  return res.json() as Promise<SavedCircuit>;
}

export async function saveCircuitCopy(
  circuit: Circuit,
  params?: CircuitGenerationParams
): Promise<SavedCircuit> {
  const res = await fetch(`${API_BASE}/api/circuits`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ circuit, params }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error((err as { error?: string }).error ?? 'Erreur lors de la sauvegarde du circuit');
  }
  return res.json() as Promise<SavedCircuit>;
}

export async function renameCircuit(id: number, name: string): Promise<SavedCircuit> {
  const res = await fetch(`${API_BASE}/api/circuits/${id}/name`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error((err as { error?: string }).error ?? 'Erreur lors du renommage');
  }
  return res.json() as Promise<SavedCircuit>;
}

/** Valide/rejette un circuit proposé par un modérateur, ou l'archive/désarchive
 * (exclusion/réintégration de la rotation des saisons) — admin uniquement. */
export async function updateCircuitStatus(
  id: number,
  status: 'published' | 'rejected' | 'archived'
): Promise<SavedCircuit> {
  const res = await fetch(`${API_BASE}/api/circuits/${id}/status`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ status }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error((err as { error?: string }).error ?? 'Erreur lors de la validation');
  }
  return res.json() as Promise<SavedCircuit>;
}

export async function deleteCircuit(id: number): Promise<void> {
  const res = await fetch(`${API_BASE}/api/circuits/${id}`, { method: 'DELETE' });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error((err as { error?: string }).error ?? 'Erreur lors de la suppression');
  }
}

export interface ImportCircuitsResult {
  imported: number;
  circuits: SavedCircuit[];
}

export async function importCircuitsBackup(
  payload: CircuitBackupFile
): Promise<ImportCircuitsResult> {
  const res = await fetch(`${API_BASE}/api/circuits/import`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error((err as { error?: string }).error ?? 'Erreur lors de l’import des circuits');
  }
  return res.json() as Promise<ImportCircuitsResult>;
}
